import type { PreviewState } from "../content/types";

/**
 * 首页用的静态示意图。
 *
 * 不是应用截图，而是按 App 同一套配色（Palette.swift）手绘的三张小场景：
 * 取景框、唱片正面、唱片架。用 SVG 而不是位图：三种场景共享同一套 CSS 变量，
 * 位图要出三张，配色一改还得重出。
 */

/** 黑胶：同心纹路 + 中心标签，与 App 里 VinylView 的构成一致。 */
function Vinyl({ cx, cy, r, label }: { cx: number; cy: number; r: number; label: "rust" | "brass" }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} className="rp-vinyl" />
      {[0.86, 0.72, 0.58].map((k) => (
        <circle key={k} cx={cx} cy={cy} r={r * k} className="rp-groove" />
      ))}
      <circle cx={cx} cy={cy} r={r * 0.34} className={label === "rust" ? "rp-label-rust" : "rp-label-brass"} />
      <circle cx={cx} cy={cy} r={r * 0.06} className="rp-spindle" />
    </g>
  );
}

/** 取景框的四角标记。画满一圈会盖住画面，App 里也只留四角。 */
function FrameCorners({ x, y, size, arm }: { x: number; y: number; size: number; arm: number }) {
  const x2 = x + size;
  const y2 = y + size;
  return (
    <path
      className="rp-frame"
      d={[
        `M ${x} ${y + arm} V ${y} H ${x + arm}`,
        `M ${x2 - arm} ${y} H ${x2} V ${y + arm}`,
        `M ${x2} ${y2 - arm} V ${y2} H ${x2 - arm}`,
        `M ${x + arm} ${y2} H ${x} V ${y2 - arm}`
      ].join(" ")}
    />
  );
}

const SHELF_ROWS = [40, 168];
const SHELF_COLS = [30, 130];

type RecordPreviewProps = {
  state: PreviewState;
  label: string;
};

export function RecordPreview({ state, label }: RecordPreviewProps) {
  return (
    <svg className="record-preview" viewBox="0 0 240 300" role="img" aria-label={label}>
      {state === "shoot" ? (
        <>
          <rect x="20" y="16" width="200" height="268" rx="16" className="rp-sleeve" />
          <rect x="44" y="54" width="152" height="152" className="rp-photo" />
          <FrameCorners x={44} y={54} size={152} arm={18} />
          <rect x="96" y="222" width="48" height="7" rx="3.5" className="rp-faint" />
          <Vinyl cx={120} cy={258} r={20} label="rust" />
        </>
      ) : null}
      {state === "record" ? (
        <>
          <Vinyl cx={152} cy={104} r={62} label="rust" />
          <rect x="24" y="42" width="124" height="124" className="rp-sleeve" />
          <rect x="36" y="54" width="100" height="100" className="rp-photo" />
          <rect x="24" y="192" width="92" height="12" rx="6" className="rp-ink" />
          <rect x="24" y="214" width="64" height="6" rx="3" className="rp-faint" />
          <line x1="24" y1="234" x2="216" y2="234" className="rp-rule" />
          {[250, 268, 286].map((y, i) => (
            <g key={y}>
              {i === 0 ? <circle cx="28" cy={y} r="3.5" className="rp-mark" /> : null}
              <rect x="40" y={y - 4} width={i === 0 ? 116 : 92} height="7" rx="3.5"
                    className={i === 0 ? "rp-ink" : "rp-faint"} />
            </g>
          ))}
        </>
      ) : null}
      {state === "shelf" ? (
        <>
          {SHELF_ROWS.map((y, row) =>
            SHELF_COLS.map((x, col) => (
              <g key={`${x}-${y}`}>
                <rect x={x} y={y} width="80" height="80" className="rp-sleeve" />
                <rect x={x + 9} y={y + 9} width="62" height="62" className="rp-photo" />
                <Vinyl cx={x + 40} cy={y + 40} r={17} label={(row + col) % 2 === 0 ? "rust" : "brass"} />
                <rect x={x + 9} y={y + 90} width="52" height="6" rx="3" className="rp-faint" />
              </g>
            ))
          )}
          <line x1="20" y1="146" x2="220" y2="146" className="rp-shelf" />
          <line x1="20" y1="274" x2="220" y2="274" className="rp-shelf" />
        </>
      ) : null}
    </svg>
  );
}
